import { collection, doc, getDoc, getDocs, runTransaction, serverTimestamp } from 'firebase/firestore';
import { db } from './firebase';
import { formatCpf, isValidCpf, normalizeCpf } from '../utils/cpf';
import { groupClientsByIdentity, normalizeEmail } from '../utils/clientIdentity';

export const CPF_ALREADY_EXISTS = 'CPF_ALREADY_EXISTS';
export const INVALID_CPF = 'INVALID_CPF';

export const findClientByCpf = async ({ companyId, cpf }) => {
  const cpfNormalized = normalizeCpf(cpf);
  if (!isValidCpf(cpfNormalized)) return null;

  const registryRef = doc(db, 'companies/' + companyId + '/clientCpfs/' + cpfNormalized);
  const registrySnap = await getDoc(registryRef);
  if (!registrySnap.exists() || !registrySnap.data().clientId) return null;

  return {
    id: registrySnap.data().clientId,
    cpf: formatCpf(cpfNormalized),
    cpfNormalized
  };
};

export const findClientByIdentity = async ({ companyId, cpf, email }) => {
  const cpfNormalized = normalizeCpf(cpf);
  const emailNormalized = normalizeEmail(email);
  if (!cpfNormalized && !emailNormalized) return null;

  const snapshot = await getDocs(collection(db, 'companies/' + companyId + '/clients'));
  const clients = groupClientsByIdentity(snapshot.docs.map(item => ({ id: item.id, ...item.data() })));
  return clients.find(client => (
    (cpfNormalized && client.clientCpfs.includes(cpfNormalized)) ||
    (emailNormalized && client.clientEmails.includes(emailNormalized))
  )) || null;
};

export const saveClientWithUniqueCpf = async ({ companyId, clientId, previousCpf, data }) => {
  const cpfNormalized = normalizeCpf(data.cpf);
  if (!isValidCpf(cpfNormalized)) {
    const error = new Error('Informe um CPF válido.');
    error.code = INVALID_CPF;
    throw error;
  }

  if (!clientId) {
    const existingClient = await findClientByIdentity({ companyId, cpf: data.cpf, email: data.email });
    if (existingClient) {
      return {
        id: existingClient.id,
        cpf: existingClient.cpf,
        cpfNormalized: existingClient.cpfNormalized,
        email: existingClient.email,
        existing: true
      };
    }
  }

  const clientRef = clientId
    ? doc(db, 'companies/' + companyId + '/clients/' + clientId)
    : doc(collection(db, 'companies/' + companyId + '/clients'));
  const registryRef = doc(db, 'companies/' + companyId + '/clientCpfs/' + cpfNormalized);
  const previousNormalized = normalizeCpf(previousCpf);
  const previousRegistryRef = previousNormalized && previousNormalized !== cpfNormalized
    ? doc(db, 'companies/' + companyId + '/clientCpfs/' + previousNormalized)
    : null;

  await runTransaction(db, async transaction => {
    const registrySnap = await transaction.get(registryRef);
    const previousRegistrySnap = previousRegistryRef ? await transaction.get(previousRegistryRef) : null;
    if (registrySnap.exists() && registrySnap.data().clientId !== clientRef.id) {
      const error = new Error('Já existe um cliente cadastrado com este CPF.');
      error.code = CPF_ALREADY_EXISTS;
      throw error;
    }
    transaction.set(clientRef, {
      ...data,
      email: String(data.email || '').trim(),
      emailNormalized: normalizeEmail(data.email),
      cpf: formatCpf(cpfNormalized),
      cpfNormalized,
      updatedAt: serverTimestamp(),
      ...(!clientId ? { createdAt: serverTimestamp() } : {})
    }, { merge: Boolean(clientId) });
    transaction.set(registryRef, {
      clientId: clientRef.id,
      cpfNormalized,
      updatedAt: serverTimestamp()
    }, { merge: true });
    if (previousRegistryRef && previousRegistrySnap?.exists() && previousRegistrySnap.data().clientId === clientRef.id) {
      transaction.delete(previousRegistryRef);
    }
  });
  return { id: clientRef.id, cpf: formatCpf(cpfNormalized), cpfNormalized };
};

export const deleteClientWithCpf = async ({ companyId, client }) => {
  const clientRef = doc(db, 'companies/' + companyId + '/clients/' + client.id);
  const cpfNormalized = normalizeCpf(client.cpfNormalized || client.cpf);
  await runTransaction(db, async transaction => {
    const registryRef = cpfNormalized ? doc(db, 'companies/' + companyId + '/clientCpfs/' + cpfNormalized) : null;
    const registrySnap = registryRef ? await transaction.get(registryRef) : null;
    transaction.delete(clientRef);
    if (registryRef && registrySnap?.exists() && registrySnap.data().clientId === client.id) {
      transaction.delete(registryRef);
    }
  });
};
